import { Component } from "@theme/component";
import { ThemeEvents } from "@theme/events";

/**
 * Gravur-Text mit Schriftauswahl.
 *
 * Schreibt Gravurtext und gewählte Schrift als Line-Item-Properties ins
 * Produktformular — die Schrift bestimmt, womit später graviert wird, und muss
 * deshalb in der Bestellung stehen. Ob die Schriftdatei vorliegt, ist dafür
 * unerheblich; sie beeinflusst nur die Vorschau.
 *
 * ⚠ Drei Fallstricke, die hier schon Bestellungen gekostet haben (17.08.2026):
 *
 * 1. Die Gravur-Konfiguration hängt am PRODUKT, die Gravurpflicht gilt aber je
 *    VARIANTE. Ohne `data-varianten` sperrt der Baustein auch „ohne
 *    Personalisierung" — der Kunde kommt dort gar nicht in den Warenkorb.
 *
 * 2. Das Theme entsperrt den Knopf bei jedem Variantenwechsel selbst
 *    (`product-form.js` → `enable()`, weil die Variante verfügbar ist). Wer
 *    hier nur beim Laden sperrt, dessen Sperre ist nach dem ersten Wechsel für
 *    immer weg. Deshalb wird auf `variantUpdate` gehört und die Prüfung
 *    NACH dem Theme erneut ausgeführt (requestAnimationFrame).
 *
 * 3. Der Knopf der Sticky-Leiste steht AUSSERHALB des Produktformulars
 *    (`sections/product-information.liquid`). Über das Formular gesucht, wird
 *    er nie gesperrt und umgeht die Gravurpflicht komplett. Gesucht wird
 *    deshalb im ganzen Abschnitt, nicht im Formular.
 *
 * Es wird bewusst KEIN interner „gesperrt"-Merker mehr geführt: Das Theme
 * greift am selben Knopf mit, ein eigener Merker läuft dadurch aus dem Tritt.
 * Wahrheit ist der DOM — erkennbar an `data-gravur-gesperrt`.
 */
class GravurText extends Component {
  connectedCallback() {
    super.connectedCallback();

    this.sectionId = this.dataset.sectionId;
    this.formId = this.dataset.formId;
    this.felder = parseInt(this.dataset.felder, 10) || 1;
    this.limit = parseInt(this.dataset.limit, 10) || 100;
    this.pflicht = this.dataset.pflicht === "true";

    // Karte { variantenId: { g: Gravur nötig, v: verfügbar } }
    try {
      this.varianten = JSON.parse(this.dataset.varianten || "{}");
    } catch {
      // Lieber Gravurpflicht für alles als stillschweigend keine Prüfung.
      this.varianten = {};
    }
    this.aktuelleVariante = this.dataset.varianteStart || null;

    this.onVariantUpdate = (event) => this.#varianteGewechselt(event);
    document.addEventListener(ThemeEvents.variantUpdate, this.onVariantUpdate);

    this.#schriftUebernehmen();
    this.#allesAktualisieren();
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    document.removeEventListener(ThemeEvents.variantUpdate, this.onVariantUpdate);
    this.#entsperren();
  }

  get #section() {
    return document.getElementById(`shopify-section-${this.sectionId}`) || document;
  }

  get #form() {
    return document.getElementById(this.formId);
  }

  #eingaben() {
    return Array.from(this.querySelectorAll(".gravur__eingabe"));
  }

  /**
   * Alle Warenkorb-Knöpfe dieses Produkts — im Formular UND in der
   * Sticky-Leiste. Der Sticky-Knopf ist `type="button"` und liegt außerhalb
   * des Formulars; über das Formular allein wird er nicht gefunden.
   */
  #knoepfe() {
    return Array.from(
      this.#section.querySelectorAll('button[type="submit"], .add-to-cart-button')
    );
  }

  /** Braucht die aktuell gewählte Variante eine Gravur? */
  #gravurNoetig() {
    if (!this.pflicht) return false;
    const eintrag = this.varianten[this.aktuelleVariante];
    // Unbekannte Variante → Pflicht. Im Zweifel lieber sperren als eine
    // Bestellung ohne Gravurangabe durchlassen.
    return eintrag ? eintrag.g !== false : true;
  }

  /** Ist die aktuelle Variante kaufbar? Unbekannt gilt als kaufbar. */
  #varianteVerfuegbar() {
    const eintrag = this.varianten[this.aktuelleVariante];
    return eintrag ? eintrag.v !== false : true;
  }

  /* ------------------------------------------------------------ Ereignisse */

  #varianteGewechselt(event) {
    const variante = event.detail?.resource;
    if (!variante?.id) return;

    this.aktuelleVariante = String(variante.id);
    if (typeof variante.available === "boolean" && this.varianten[this.aktuelleVariante]) {
      this.varianten[this.aktuelleVariante].v = variante.available;
    }

    // Erst nachdem das Theme seinen eigenen Knopf-Zustand gesetzt hat, sonst
    // überschreibt `enable()` die Sperre wieder (siehe Fallstrick 2 oben).
    requestAnimationFrame(() => this.#allesAktualisieren());
  }

  textGeaendert(event) {
    // event.target — das Theme reicht Handler über einen globalen Listener nach.
    const feld = event.target;
    const index = parseInt(feld.dataset.index, 10);

    const zaehler = this.querySelector(`.gravur__zaehler[data-index="${index}"]`);
    if (zaehler) {
      zaehler.textContent = `${feld.value.length}/${this.limit}`;
      zaehler.classList.toggle("is-voll", feld.value.length >= this.limit);
    }

    this.#allesAktualisieren();
  }

  schriftGeaendert() {
    this.#schriftUebernehmen();
    this.#allesAktualisieren();
  }

  /** Setzt die Vorschau auf die gewählte Schrift. */
  #schriftUebernehmen() {
    const wahl = this.refs.schriftwahl;
    const vorschau = this.refs.vorschauText;
    if (!wahl || !vorschau) return;

    const stil = wahl.options[wahl.selectedIndex]?.dataset.stil || "1";
    vorschau.className = `gravur__vorschau-text gravur__schrift--${stil}`;
  }

  /* --------------------------------------------------------- Aktualisieren */

  #allesAktualisieren() {
    const aktiv = this.#gravurNoetig();

    // Bei „ohne Personalisierung" verschwindet der ganze Baustein — samt
    // Widerrufshinweis, der dort sachlich falsch wäre.
    this.classList.toggle("gravur-ausgeblendet", !aktiv);

    const werte = this.#eingaben().map((f) => f.value.trim());

    // Nur gefüllte Felder gehen mit; leere sind deaktiviert und werden vom
    // Browser nicht gesendet. Bei einer Variante ohne Gravur geht gar nichts
    // mit — sonst hinge ein Gravurtext an einem unpersonalisierten Artikel.
    this.#eingaben().forEach((feld) => {
      const index = feld.dataset.index;
      const ausgabe = this.querySelector(`input[type="hidden"][data-index="${index}"]`);
      if (!ausgabe) return;
      const wert = feld.value.trim();
      ausgabe.disabled = !aktiv || wert === "";
      ausgabe.value = aktiv ? wert : "";
    });

    // Schrift nur mitschicken, wenn überhaupt etwas graviert wird.
    const wahl = this.refs.schriftwahl;
    const schriftAusgabe = this.refs.schriftAusgabe;
    const etwasGraviert = aktiv && werte.some((w) => w !== "");
    if (wahl && schriftAusgabe) {
      schriftAusgabe.value = wahl.value;
      schriftAusgabe.disabled = !etwasGraviert;
    }

    this.#vorschauAktualisieren(aktiv ? werte : []);
    this.#pruefen(aktiv ? werte : null);
  }

  #vorschauAktualisieren(werte) {
    const kasten = this.refs.vorschau;
    const text = this.refs.vorschauText;
    if (!kasten || !text) return;

    const inhalt = werte.filter(Boolean).join(" · ");
    kasten.hidden = inhalt === "";
    text.textContent = inhalt;
  }

  /** `werte === null` heißt: diese Variante braucht keine Gravur. */
  #pruefen(werte) {
    if (werte === null) {
      this.#meldung(null);
      this.#entsperren();
      return;
    }

    const leer = werte.every((w) => w === "");
    this.#meldung(leer ? this.dataset.meldungFehlt : null);
    if (leer) this.#sperren();
    else this.#entsperren();
  }

  #meldung(text) {
    const kasten = this.refs.hinweis;
    const inhalt = this.refs.hinweisText;
    if (!kasten || !inhalt) return;
    kasten.hidden = !text;
    if (text) inhalt.textContent = text;
  }

  #sperren() {
    this.#knoepfe().forEach((b) => {
      b.setAttribute("disabled", "");
      b.dataset.gravurGesperrt = "true";
    });
    this.#section
      .querySelectorAll(".shopify-payment-button, .accelerated-checkout-block")
      .forEach((el) => el.classList.add("gravur-ausgeblendet"));
  }

  #entsperren() {
    // Nur die eigene Sperre lösen. Ein vom Theme wegen „ausverkauft"
    // gesetztes `disabled` trägt die Markierung nicht und bleibt unangetastet.
    const kaufbar = this.#varianteVerfuegbar();

    this.#knoepfe()
      .filter((b) => b.dataset.gravurGesperrt === "true")
      .forEach((b) => {
        delete b.dataset.gravurGesperrt;
        if (kaufbar) b.removeAttribute("disabled");
      });

    this.#section
      .querySelectorAll(".shopify-payment-button.gravur-ausgeblendet, .accelerated-checkout-block.gravur-ausgeblendet")
      .forEach((el) => el.classList.remove("gravur-ausgeblendet"));
  }
}

if (!customElements.get("gravur-text")) {
  customElements.define("gravur-text", GravurText);
}
